// SPDX-License-Identifier: MIT
// /download — public beta download page (Genesis Desktop). Served from the VPS
// (Caddy) like the rest of the site; the binaries are static files under /dl/.
// Linux is live (built on the VPS); macOS/Windows land once GitHub Actions
// minutes reset (the build matrix is ready, just billing-gated). No session needed.
import { t } from '../i18n/t';
import { escapeHtml } from '../lib/escape';
import { renderHeader } from '../components/header';
import { renderFooter } from '../components/footer';
import type { Locale } from '../i18n/detect';

export interface DownloadPageOptions {
  locale?: Locale;
  // Authed chrome (best-effort) — ✦ idées pill + account menu when signed in.
  user?: { email: string };
  onSignOut?: () => void | Promise<void>;
}

// Bump on each beta + drop the matching files under /srv/genesis-web/dist/dl/.
// Linux + the page tag track BETA_VERSION; Windows is decoupled (WIN_VERSION) —
// it's now a NATIVE MSVC build (built in the Windows VM, ~200 MB), no longer the
// old cross-compiled MinGW `-connect` stub. Keep them independent so a Windows
// rebuild doesn't point the Linux buttons at files that don't exist.
const BETA_VERSION = '2.7.1';
// 2026-06-23: the download PAGE was stuck at 2.7.1 while the desktop shipped through
// 2.7.16 (latest.json + auto-updater were bumped, the page was not — operator caught
// it). Windows is the current dogfood target: bump to 2.7.16 (the NSIS .exe exists +
// serves HTTP 200). mac/Linux keep 2.7.1 — no newer .dmg/.AppImage artifact exists, so
// pointing them higher would 404 (per the decoupling note above).
const WIN_VERSION = '2.7.18';
const MAC_VERSION = '2.7.1';
const DL = (file: string) => `/dl/${file}`;
const LINUX_APPIMAGE = DL(`Genesis_${BETA_VERSION}_amd64.AppImage`);
const LINUX_DEB = DL(`Genesis_${BETA_VERSION}_amd64.deb`);
// Windows .exe — native MSVC + NSIS (the real installer, current).
const WINDOWS_EXE = DL(`Genesis_${WIN_VERSION}_x64-setup.exe`);
// macOS — unsigned per-arch .dmg from the GitHub Actions macOS runners (no universal
// merge step, so two builds: Apple Silicon + Intel). Beta like the others; Gatekeeper
// bypass on first open is covered by download.mac.howto.
const MAC_DMG_ARM = DL(`Genesis_${MAC_VERSION}_aarch64.dmg`);
const MAC_DMG_X64 = DL(`Genesis_${MAC_VERSION}_x64.dmg`);

export function renderDownloadPage(opts: DownloadPageOptions = {}): DocumentFragment {
  const frag = document.createDocumentFragment();
  frag.appendChild(renderHeader({ locale: opts.locale, activePath: '/download', currentRung: 'desktop', user: opts.user, onSignOut: opts.onSignOut }));

  const main = document.createElement('main');
  main.className = 'dl';
  main.setAttribute('data-component', 'download-page');

  // An available OS card (Linux) carries one or more real download buttons; a
  // "soon" card (mac/win) is dimmed with a badge. The how-to line is the
  // unsigned-warning contournement, OS-specific.
  const available = (os: 'linux' | 'win' | 'mac', btns: string) => `
    <article class="dl-os dl-os--${os} is-available">
      <div class="dl-os__head"><span class="dl-os__name">${escapeHtml(t(`download.${os}.name`))}</span>
        <span class="dl-os__status">${escapeHtml(t(`download.${os}.status`))}</span></div>
      <div class="dl-os__btns">${btns}</div>
      <p class="dl-os__howto">${escapeHtml(t(`download.${os}.howto`))}</p>
    </article>`;

  const linuxBtns = `
    <a class="dl-os__btn dl-os__btn--primary" href="${LINUX_APPIMAGE}" download>${escapeHtml(t('download.linux.appimage'))}</a>
    <a class="dl-os__btn" href="${LINUX_DEB}" download>${escapeHtml(t('download.linux.deb'))}</a>`;
  const winBtns = `
    <a class="dl-os__btn dl-os__btn--primary" href="${WINDOWS_EXE}" download>${escapeHtml(t('download.win.exe'))}</a>`;
  const macBtns = `
    <a class="dl-os__btn dl-os__btn--primary" href="${MAC_DMG_ARM}" download>${escapeHtml(t('download.mac.dmgArm'))}</a>
    <a class="dl-os__btn" href="${MAC_DMG_X64}" download>${escapeHtml(t('download.mac.dmgIntel'))}</a>`;

  main.innerHTML = `
    <span class="dl__tag">${escapeHtml(t('download.tag'))} · ${escapeHtml(BETA_VERSION)}</span>
    <h1 class="dl__title">${escapeHtml(t('download.title'))}</h1>
    <p class="dl__lead">${escapeHtml(t('download.lead'))}</p>
    <section class="dl__grid">
      ${available('linux', linuxBtns)}
      ${available('win', winBtns)}
      ${available('mac', macBtns)}
    </section>
    <p class="dl__unsigned">${escapeHtml(t('download.unsigned'))}</p>
    <a class="dl__back" data-nav href="/facons">${escapeHtml(t('download.back'))}</a>
  `;
  frag.appendChild(main);
  frag.appendChild(renderFooter());
  return frag;
}
